import { useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import {
  Archive,
  ArrowLeft,
  BellOff,
  GripVertical,
  Pin,
  PinOff,
  MessageSquarePlus,
  MoreVertical,
  Search,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import { useDebounceValue } from "usehooks-ts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty-state";
import { useLocale } from "@/hooks/useLocale";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import {
  useArchiveChatConversationMutation,
  usePinChatConversationMutation,
  useReorderPinnedChatsMutation,
  useGetChatConversationsInfiniteQuery,
} from "@/services/(my-space)/api.chat";
import { chatHub } from "@/services/websocket/chat-hub";
import { SOCKET_CHAT_EVENTS } from "@/services/websocket/socket.enum";
import { getErrorMessage } from "@/utils/error.utils";
import { cn } from "@/utils/cn";
import type {
  ChatListTab,
  IChatConversation,
  IChatMessage,
  IChatTypingEvent,
} from "@/types/(my-space)/chat.interface";
import { ChatAvatar } from "./ChatAvatar";
import { DeleteChatDialog } from "./DeleteChatDialog";
import type { NewChatStart } from "./NewChatDialogs";
import { ProfilePopup } from "./ProfilePopup";
import { ChatSearchResults } from "./ChatSearchResults";
import { isSearchableQuery } from "../_lib/message-search";
import { conversationTime, conversationTitle, lastMessagePreview } from "../_lib/chat-labels";
import { usePresenceVersion, useWatchPresence } from "../_hooks/usePresence";
import { useChatListParams } from "../params";
import { withPinnedOrder } from "../_lib/pinned";
import { channelOf, isOfficialChannel } from "../_lib/channel-notices";
import { VerifiedBadge } from "./VerifiedBadge";

const TABS: ChatListTab[] = ["all", "unread", "direct", "groups"];
const TYPING_TTL_MS = 6000;

/** conversationId → who is typing there, expiring like the room's indicator. */
const useListTyping = (myUserId: number | undefined) => {
  const [typing, setTyping] = useState<
    Map<number, { userId: number; at: number; recording: boolean; name: string | null }>
  >(new Map());
  useEffect(
    () =>
      chatHub.subscribe((event, payload) => {
        if (event === SOCKET_CHAT_EVENTS.TYPING) {
          const e = payload as IChatTypingEvent;
          if (e.userId === myUserId) return;
          setTyping((map) => {
            const next = new Map(map);
            if (e.isTyping) {
              next.set(e.conversationId, {
                userId: e.userId,
                at: Date.now(),
                recording: e.activity === "recording",
                name: e.name ?? null,
              });
            }
            else if (next.get(e.conversationId)?.userId === e.userId) next.delete(e.conversationId);
            return next;
          });
        } else if (event === SOCKET_CHAT_EVENTS.MESSAGE_NEW) {
          const m = payload as IChatMessage;
          setTyping((map) => {
            if (map.get(m.conversationId)?.userId !== m.sender?.id) return map;
            const next = new Map(map);
            next.delete(m.conversationId);
            return next;
          });
        }
      }),
    [myUserId],
  );
  useEffect(() => {
    if (!typing.size) return;
    const timer = setInterval(() => {
      const cutoff = Date.now() - TYPING_TTL_MS;
      setTyping((map) => {
        const next = new Map([...map].filter(([, v]) => v.at >= cutoff));
        return next.size === map.size ? map : next;
      });
    }, 2000);
    return () => clearInterval(timer);
  }, [typing.size]);
  return typing;
};

interface ConversationListProps {
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Open the chat with its Chat Info drawer showing. */
  onOpenInfo: (id: number) => void;
  /** "Create new": its menu, or (the Groups filter, empty) the new-group form. */
  onNew: (start?: NewChatStart) => void;
  /** A chat was deleted (for me) — leave it if it's the open one. */
  onDeleted: (id: number) => void;
  /** A message search result: open its chat on that message. */
  onOpenMessage: (conversationId: number, messageId: number) => void;
}

export const ConversationList = ({
  selectedId,
  onSelect,
  onOpenInfo,
  onNew,
  onDeleted,
  onOpenMessage,
}: ConversationListProps) => {
  const { t } = useTranslation("chat");
  const [toDelete, setToDelete] = useState<number | null>(null);
  const [profile, setProfile] = useState<IChatConversation | null>(null);
  const myUserId = useAppSelector(selectUser).user?.id;
  const params = useChatListParams();
  const { archived, tab, search, setSearch, setTab, setArchived } = params;
  const [debouncedSearch] = useDebounceValue(search, 300);
  const noGroups = !archived && tab === "groups";

  const query = useGetChatConversationsInfiniteQuery({
    tab: archived ? "all" : tab,
    archived,
    search: debouncedSearch,
  });
  // A drop shows at once; the refetch it triggers replaces it.
  const [pinOrder, setPinOrder] = useState<number[] | null>(null);
  const conversations = useMemo(
    () => withPinnedOrder(query.data?.pages.flatMap((p) => p.data) ?? [], pinOrder),
    [query.data, pinOrder],
  );
  useEffect(() => setPinOrder(null), [query.data]);
  const pinnedIds = conversations.filter((c) => c.isPinned).map((c) => c.id);
  // Only the full list holds all my pins in order (not a filter or a search).
  const canReorderPins = !archived && tab === "all" && !debouncedSearch.trim() && pinnedIds.length > 1;
  const [reorderPinned] = useReorderPinnedChatsMutation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onPinnedDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(pinnedIds, pinnedIds.indexOf(Number(active.id)), pinnedIds.indexOf(Number(over.id)));
    setPinOrder(next);
    try {
      await reorderPinned(next).unwrap();
    } catch (e) {
      setPinOrder(null);
      toast.error(getErrorMessage(e, t("errors.pin_chat")));
      void query.refetch();
    }
  };

  const typing = useListTyping(myUserId);
  useWatchPresence(
    // A self chat's peer is me: no presence to watch.
    conversations
      .filter((c) => c.type === "direct" && !c.isSelf && c.peer)
      .map((c) => c.peer!.id),
  );
  usePresenceVersion();

  // Infinite scroll: fetch the next page as the sentinel comes into view.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const title = archived ? t("page.archived_title") : t("page.conversations");
  // Searching (not in Archived): chats, people and messages, not just chats.
  const searchTerm = debouncedSearch.trim();
  const searching = !archived && isSearchableQuery(searchTerm);
  const renderRow = (c: IChatConversation, sortable: boolean) => (
    <ConversationRow
      key={c.id}
      conversation={c}
      myUserId={myUserId}
      selected={c.id === selectedId}
      typingUserId={typing.get(c.id)?.userId}
      typingRecording={typing.get(c.id)?.recording}
      typingName={typing.get(c.id)?.name}
      sortable={sortable}
      onSelect={() => onSelect(c.id)}
      onDelete={() => setToDelete(c.id)}
      // A channel has no profile: its picture just opens it.
      onAvatarClick={() => (c.type === "channel" ? onSelect(c.id) : setProfile(c))}
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 px-4 pt-4 pb-3">
        {archived ? (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setArchived(false)}
            aria-label={t("list.back")}
          >
            <ArrowLeft className="size-4 rtl-flip" />
          </Button>
        ) : null}
        <h1 className="flex-1 truncate text-lg font-semibold text-foreground">{title}</h1>
        {!archived ? (
          <>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setArchived(true)}
              aria-label={t("list.archived")}
              title={t("list.archived")}
            >
              <Archive className="size-4" />
            </Button>
            <Button
              variant="primary"
              size="icon-sm"
              onClick={() => onNew()}
              aria-label={t("list.new_chat")}
              title={t("list.new_chat")}
            >
              <MessageSquarePlus className="size-4" />
            </Button>
          </>
        ) : null}
      </div>

      <div className="px-4 pb-3">
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("list.search")}
            aria-label={t("list.search")}
            className="h-10 bg-surface ps-9"
          />
        </div>
      </div>

      {!archived ? (
        <div className="flex gap-1.5 overflow-x-auto px-4 pb-3 scrollbar-hidden" role="tablist">
          {TABS.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={cn(
                "shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                tab === value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-surface text-muted-foreground hover:bg-muted",
              )}
            >
              {t(`list.tab_${value}`)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {searching ? (
          <ChatSearchResults
            query={searchTerm}
            chats={conversations}
            chatsLoading={query.isLoading}
            renderChat={(c) => renderRow(c, false)}
            myUserId={myUserId}
            onOpenChat={onSelect}
            onOpenMessage={onOpenMessage}
          />
        ) : query.isLoading ? (
          <ListSkeleton />
        ) : conversations.length === 0 ? (
          <Empty className="border-0 p-6">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <MessageSquarePlus />
              </EmptyMedia>
              <EmptyTitle className="text-base">
                {debouncedSearch.trim()
                  ? t("list.no_results", { query: debouncedSearch.trim() })
                  : archived
                    ? t("list.archived_empty")
                    : noGroups
                      ? t("list.groups_empty")
                      : t("list.empty")}
              </EmptyTitle>
              {!archived && !debouncedSearch.trim() ? (
                <EmptyDescription>
                  {noGroups ? t("list.groups_empty_hint") : t("list.empty_hint")}
                </EmptyDescription>
              ) : null}
            </EmptyHeader>
            {!archived && !debouncedSearch.trim() ? (
              // On the Groups filter it's a group they're after: straight to that form.
              <Button variant="primary" size="sm" onClick={() => onNew(noGroups ? "group" : "menu")}>
                {noGroups ? t("list.new_group") : t("list.new_chat")}
              </Button>
            ) : null}
          </Empty>
        ) : (
          <ul className="flex flex-col pb-2">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onPinnedDragEnd}>
              <SortableContext items={pinnedIds} strategy={verticalListSortingStrategy}>
                {conversations.map((c) => renderRow(c, canReorderPins && c.isPinned))}
              </SortableContext>
            </DndContext>
            <div ref={sentinelRef} className="h-1" />
            {isFetchingNextPage ? <ListSkeleton rows={2} /> : null}
          </ul>
        )}
      </div>
      <ProfilePopup
        conversation={profile}
        onClose={() => setProfile(null)}
        onMessage={onSelect}
        onInfo={onOpenInfo}
      />
      <DeleteChatDialog
        conversationId={toDelete}
        isSelf={conversations.find((c) => c.id === toDelete)?.isSelf}
        onClose={() => setToDelete(null)}
        onDeleted={onDeleted}
      />
    </div>
  );
};

const ListSkeleton = ({ rows = 6 }: { rows?: number }) => (
  <div className="flex flex-col gap-1 px-2">
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-3 px-2 py-3">
        <Skeleton className="size-11 rounded-full" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-3.5 w-1/2" />
          <Skeleton className="h-3 w-3/4" />
        </div>
      </div>
    ))}
  </div>
);

interface ConversationRowProps {
  conversation: IChatConversation;
  myUserId: number | undefined;
  selected: boolean;
  typingUserId?: number;
  /** That person is recording a voice note, not typing. */
  typingRecording?: boolean;
  typingName?: string | null;
  /** One of my pinned chats that can be dragged into another order. */
  sortable?: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onAvatarClick: () => void;
}

const ConversationRow = ({
  conversation: c,
  myUserId,
  selected,
  typingUserId,
  typingRecording = false,
  typingName,
  sortable = false,
  onSelect,
  onDelete,
  onAvatarClick,
}: ConversationRowProps) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const [archive] = useArchiveChatConversationMutation();
  const [pin] = usePinChatConversationMutation();
  const name = conversationTitle(t, c);
  const isOnline =
    c.type === "direct" && !c.isSelf && !!chatHub.getPresence(c.peer?.id)?.online;
  const unread = c.unreadCount;
  // Only while unread: reading the chat clears both on the next list fetch.
  const mentions = unread > 0 ? (c.unreadMentionCount ?? 0) : 0;

  const preview =
    typingUserId != null && !c.isSelf
      ? c.type === "direct"
        ? typingRecording
          ? t("status.recording")
          : t("status.typing")
        : typingRecording
          ? t("status.user_recording", { name: typingName || t("system.someone") })
          : t("status.user_typing", { name: typingName || t("system.someone") })
      : lastMessagePreview(t, c, myUserId);

  const toggleArchive = async () => {
    try {
      await archive({ id: c.id, archived: !c.isArchived }).unwrap();
      toast.success(c.isArchived ? t("info.unarchived_toast") : t("info.archived_toast"));
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.archive")));
    }
  };

  const togglePin = async () => {
    try {
      await pin({ id: c.id, pinned: !c.isPinned }).unwrap();
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.pin_chat")));
    }
  };

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: c.id, disabled: !sortable });

  return (
    <li
      ref={setNodeRef}
      style={sortable ? { transform: CSS.Translate.toString(transform), transition } : undefined}
      // Drag from anywhere on the row; only the handle takes keys (a key on the
      // row's own buttons must not pick it up).
      onPointerDown={sortable ? (listeners?.onPointerDown as React.PointerEventHandler | undefined) : undefined}
      className={cn(
        "group relative bg-background px-2",
        isDragging && "z-10 rounded-sm shadow-md ring-1 ring-border",
      )}
    >
      {sortable ? (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label={t("list.reorder_pinned", { name })}
          title={t("list.reorder_pinned", { name })}
          className="absolute start-0 top-1/2 z-10 flex h-8 w-3.5 -translate-y-1/2 cursor-grab touch-none items-center justify-center rounded-xs text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 active:cursor-grabbing"
        >
          <GripVertical className="size-3.5" />
        </button>
      ) : null}
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? "true" : undefined}
        className={cn(
          "flex w-full items-center gap-3 rounded-sm px-2 py-2.5 text-start transition-colors",
          selected ? "bg-primary/10" : "hover:bg-muted",
        )}
      >
        <ChatAvatar
          name={name}
          imageUrl={c.type === "direct" ? c.peer?.profileImage : c.imageUrl}
          kind={c.type}
          channel={channelOf(c)}
          size="lg"
          isOnline={isOnline}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "truncate text-sm text-foreground",
                unread > 0 ? "font-semibold" : "font-medium",
              )}
            >
              {name}
            </span>
            {isOfficialChannel(channelOf(c)) ? <VerifiedBadge /> : null}
            {c.isMuted ? (
              <BellOff className="size-3.5 shrink-0 text-muted-foreground" aria-label={t("a11y.muted")} />
            ) : null}
            <span
              className={cn(
                "ms-auto shrink-0 text-[11px]",
                unread > 0 ? "font-semibold text-primary" : "text-muted-foreground",
              )}
            >
              {conversationTime(t, formatDate, c.lastMessage?.createdAt ?? c.lastActivityAt)}
            </span>
          </div>
          <div className="flex items-center gap-2 pe-6">
            <span
              className={cn(
                "truncate text-xs",
                typingUserId != null && !c.isSelf
                  ? "font-medium text-primary"
                  : unread > 0
                    ? "text-foreground"
                    : "text-muted-foreground",
              )}
            >
              {preview}
            </span>
            {c.isPinned ? (
              // Like WhatsApp: the pin sits before the badges.
              <Pin
                className="ms-auto size-3.5 shrink-0 text-muted-foreground"
                role="img"
                aria-label={t("a11y.pinned_chat")}
              />
            ) : null}
            {mentions > 0 ? (
              // Like WhatsApp: an "@" beside the count when one of them tags
              // me — loud even in a muted chat, as its push is.
              <span
                className={cn(
                  !c.isPinned && "ms-auto",
                  "flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground",
                )}
                aria-label={t("mentions.unread_mentions", { count: mentions })}
                title={t("mentions.you_were_mentioned")}
              >
                @
              </span>
            ) : null}
            {unread > 0 ? (
              <span
                className={cn(
                  mentions > 0 || c.isPinned ? "" : "ms-auto",
                  "flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-bold",
                  c.isMuted ? "bg-muted-foreground/60 text-background" : "bg-primary text-primary-foreground",
                )}
                aria-label={t("a11y.unread_count", { count: unread })}
              >
                {unread > 99 ? "99+" : unread}
              </span>
            ) : null}
          </div>
        </div>
      </button>
      {/* The picture opens the profile popup; the rest of the row opens the
          chat. A sibling over the avatar — a button can't hold a button. */}
      <button
        type="button"
        onClick={onAvatarClick}
        aria-label={t("profile.open", { name })}
        className="absolute start-4 top-1/2 size-12 -translate-y-1/2 rounded-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={t("actions.more")}
            className="absolute end-3 bottom-2.5 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
          >
            <MoreVertical className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={togglePin}>
            {c.isPinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
            {c.isPinned ? t("info.unpin_chat") : t("info.pin_chat")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={toggleArchive}>
            <Archive className="size-4" />
            {c.isArchived ? t("info.unarchive") : t("info.archive")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-danger focus:text-danger" onSelect={onDelete}>
            <Trash2 className="size-4" />
            {t("info.delete_chat")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
};
